import { type Dispatch, useCallback, useEffect, useRef } from 'react';

import { NODE_NAME } from '@utils/eventBus/constants';

export type CallbackEvent = (event: Event) => void;

export type UseEventBus = {
  publish: <T = unknown>(eventName: string, payload?: T) => void;
  subscribe: <T>(eventName: string, callback: Dispatch<T>) => CallbackEvent;
  unsubscribe: (eventName: string, callback: CallbackEvent) => void;
};

function throwBusNotFound(): never {
  throw new Error('EventBus не найден');
}

function findComment(): Comment | null {
  const nodeIterator = document.createNodeIterator(
    document.body,
    NodeFilter.SHOW_COMMENT,
  );
  const comment = nodeIterator.nextNode() as Comment | null;

  if (comment && 'data' in comment && comment.data === NODE_NAME) {
    return comment;
  }

  return null;
}

export const useEventBus = (): UseEventBus => {
  const element = useRef<Comment>();

  const reassignElement = useCallback(() => {
    const comment = findComment();
    if (comment) {
      element.current = comment;
    } else {
      throwBusNotFound();
    }
  }, []);

  useEffect(() => {
    const comment = findComment();

    if (comment) {
      element.current = comment;
    }
  }, []);

  const publish = useCallback(
    <T = unknown>(eventName: string, payload?: T) => {
      if (!element.current) {
        reassignElement();
      }
      const event = new CustomEvent(eventName, {
        detail: payload,
      });
      element.current!.dispatchEvent(event);
    },
    [reassignElement],
  );

  const subscribe = useCallback(
    <T>(eventName: string, callback: Dispatch<T>) => {
      if (!element.current) {
        reassignElement();
      }
      const callbackEvent = (event: Event): void => {
        callback((event as CustomEvent<T>).detail);
      };
      element.current!.addEventListener(eventName, callbackEvent);
      return callbackEvent;
    },
    [reassignElement],
  );

  const unsubscribe = useCallback(
    (eventName: string, callback: (event: Event) => void) => {
      element.current?.removeEventListener(eventName, callback);
    },
    [],
  );

  return {
    publish,
    subscribe,
    unsubscribe,
  };
};
